import { type FC } from 'react';
import InputCalendar from '@components/InputCalendar';
import InputMultiSelect from '@components/InputMultiSelect';
import { COMPACT_MULTI_SELECT_STYLE_PROPS } from '@components/InputMultiSelect/constants';
import { Show } from '@components/Show';
import type { BlockEditFormState } from '@ContractBuilder/modules/block-edit';
import { selectAssignableTeams } from '@helpers/teams';
import { formatDate } from '@root/helpers';
import { ALL_CLASSES_OF_BUSINESS } from '@root/helpers/constants';

import type { BlockDetails } from '../hooks/useBlockDetails';
import { useBlockPanelContent } from '../hooks/useBlockPanelContent';
import type { BlockEditFormSetter } from '../types';
import { getExpiryDatePickerProps, getInceptionDatePickerProps } from '../utils/get-date-picker-props';
import type { PanelMode } from '../utils/get-panel-mode';

import { ClauseDetails } from './ClauseDetails';
import type { DatapointDisplayKey } from './DatapointDisplay';
import { DatapointDisplay } from './DatapointDisplay';
import { DrawerBlockStatus } from './DrawerBlockStatus';
import { DrawerBlockUsedWithin } from './DrawerBlockUsedWithin';
import { DrawerCollapsibleGroup } from './DrawerCollapsibleGroup';
import { DrawerLastUpdatedBy } from './DrawerLastUpdatedBy';
import { DrawerParentBlockName } from './DrawerParentBlockName';

interface DrawerPanelDetailsProps {
  details: BlockDetails;
  formValues?: BlockEditFormState;
  isBlockType: boolean;
  mode: PanelMode;
  setFormValues: BlockEditFormSetter;
}

export const DrawerPanelDetails: FC<DrawerPanelDetailsProps> = ({
  details,
  formValues,
  isBlockType,
  mode,
  setFormValues,
}) => {
  const { blockStatus, blockUpdatedAt, blockUpdatedByName, hasBlockBeenUpdatedOnContract, isFetchingTeams, teams } =
    details;
  const { isAdminView, isBlock, isClause, isLibraryBlockEdit } = mode;

  const isClauseType = !isBlockType;
  const { getContent } = useBlockPanelContent(isBlockType);

  if (!formValues) return null;

  if (details.isHidden) {
    return null;
  }

  const setDatapointValue =
    <K extends DatapointDisplayKey>(inputKey: K) =>
    (value: BlockEditFormState[K] | null | undefined) =>
      setFormValues((prev) => ({ ...prev, [inputKey]: value }) as Partial<BlockEditFormState>);

  return (
    <DrawerCollapsibleGroup
      title={getContent('groups.details')}
      id="details"
      className={isLibraryBlockEdit ? '!border-t-0' : undefined}
    >
      <Show when={isAdminView}>
        <DrawerBlockStatus
          isArchived={blockStatus.isArchived}
          isBlock={isBlockType}
          isInactive={blockStatus.isInactive}
          isNotYetActive={blockStatus.isNotYetActive}
        />
        <DrawerParentBlockName isBlock={isBlockType} parentBlockId={formValues?.parentBlockId} />
        <Show when={!isBlock && !isClause && blockUpdatedAt && blockUpdatedByName}>
          <DrawerLastUpdatedBy
            title={getContent('fields.lastUpdated.labelOnTemplates')}
            updatedAt={blockUpdatedAt ?? ''}
            updatedByName={blockUpdatedByName ?? ''}
          />
        </Show>
      </Show>
      <Show when={!isAdminView && blockUpdatedAt && blockUpdatedByName && hasBlockBeenUpdatedOnContract}>
        <DrawerLastUpdatedBy
          title={getContent('fields.lastUpdated.labelOnContracts')}
          updatedAt={blockUpdatedAt ?? ''}
          updatedByName={blockUpdatedByName ?? ''}
        />
      </Show>
      <DatapointDisplay<'inception_date'>
        getContractValuePillsLabel={(value) => formatDate(value, 'DD MMMM YYYY') ?? ''}
        value={formValues.inception_date}
        onChange={setDatapointValue('inception_date')}
        isAdminView={isAdminView}
        renderInput={(props) => (
          <InputCalendar
            {...props}
            dayPickerProps={getInceptionDatePickerProps(formValues?.expiry_date)}
            emptyValue={null}
            shouldRenderInsidePortal
            wrapperClassName="ml-0"
            dataTestId="calendar:inception-date"
          />
        )}
        title={getContent('fields.inceptionDate.label')}
      />
      <DatapointDisplay<'expiry_date'>
        getContractValuePillsLabel={(value) => formatDate(value, 'DD MMMM YYYY') ?? ''}
        value={formValues.expiry_date}
        onChange={setDatapointValue('expiry_date')}
        isAdminView={isAdminView}
        renderInput={(props) => (
          <InputCalendar
            {...props}
            dayPickerProps={getExpiryDatePickerProps(formValues?.inception_date)}
            emptyValue={null}
            shouldRenderInsidePortal
            wrapperClassName="ml-0"
            dataTestId="calendar:expiry-date"
          />
        )}
        title={getContent('fields.expiryDate.label')}
      />
      <Show when={isClauseType}>
        <ClauseDetails
          formValues={formValues}
          isAdminView={isAdminView}
          isBlockType={isBlockType}
          setFormValues={setFormValues}
        />
      </Show>
      <DatapointDisplay<'teams'>
        value={formValues.teams}
        onChange={setDatapointValue('teams')}
        title={getContent('fields.teamFilter.label')}
        isAdminView={isAdminView}
        isLoading={isFetchingTeams}
        getContractValuePillsLabel={(value) => teams?.find((t) => t.id === value)?.name ?? ''}
        renderInput={(props) => (
          <InputMultiSelect<string | number>
            {...props}
            isSearchable
            isReadOnly={!isAdminView}
            labelText=""
            options={selectAssignableTeams(teams ?? [], formValues.teams).map((team) => ({
              name: team.name,
              value: team.id,
            }))}
            dataTestId="multiselect:teams"
            className="max-w-full min-w-0"
            {...COMPACT_MULTI_SELECT_STYLE_PROPS}
          />
        )}
      />
      <DatapointDisplay<'class_of_business'>
        value={formValues.class_of_business}
        onChange={setDatapointValue('class_of_business')}
        title={getContent('fields.classOfBusiness.label')}
        isAdminView={isAdminView}
        renderInput={(props) => (
          <InputMultiSelect
            {...props}
            isSearchable
            labelText=""
            options={ALL_CLASSES_OF_BUSINESS.map((label) => ({ name: label, value: label }))}
            dataTestId="multiselect:class-of-business"
            className="max-w-full min-w-0"
            {...COMPACT_MULTI_SELECT_STYLE_PROPS}
          />
        )}
      />
      <Show when={isAdminView}>
        <DrawerBlockUsedWithin data={formValues?.usedInTemplates} isBlock={isBlockType} />
      </Show>
    </DrawerCollapsibleGroup>
  );
};
